import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Car, ArrowRight, Calendar, Settings, Fuel, Users, Gauge, Star } from "lucide-react";
import { localize, imgV, hasPromo, termPrice, priceExVat, vatPrice, fmtKc } from "@/data/vehicles";
import { useVatMode, setVatMode } from "@/lib/vatMode";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang } from "@/i18n/LanguageContext";
import { useVehicles } from "@/context/VehiclesContext";
import TermSwitch from "@/components/TermSwitch";

const Spec = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.04] border border-white/[0.06] px-2.5 py-1">
    <Icon className="w-3.5 h-3.5 text-orange-400" /> {children}
  </span>
);

// Single car card with thumbnail hover/tap to switch the main image
export const VehicleCard = ({ vehicle, months }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const vat = useVatMode();
  const mainImage = vehicle.images?.[activeIndex] || vehicle.images?.[0];
  const cardRef = useRef(null);

  const canHover = typeof window !== "undefined" && window.matchMedia?.("(hover: hover)").matches;
  const handleMove = (e) => {
    if (!canHover) return;
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transition = "transform 0.06s linear";
    el.style.transform = `perspective(900px) rotateX(${(-py * 4).toFixed(2)}deg) rotateY(${(px * 5).toFixed(2)}deg) translateY(-8px)`;
  };
  const handleLeave = () => {
    const el = cardRef.current;
    if (!el) return;
    el.style.transition = "";
    el.style.transform = "";
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className="car-card rounded-2xl overflow-hidden flex flex-col"
      data-testid={`vehicle-card-${vehicle.slug}`}
    >
      {/* Main image — click to open detail */}
      <div
        className="relative aspect-[4/3] overflow-hidden cursor-pointer group"
        onClick={() => navigate(`/vozy/${vehicle.slug}`)}
        data-testid={`vehicle-main-image-${vehicle.slug}`}
      >
        {mainImage ? (
          <img
            src={imgV(mainImage.url, "card")}
            alt={mainImage.alt}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-white/5 text-zinc-600">
            <Car className="w-10 h-10" />
          </div>
        )}

        {vehicle.createdAt && (Date.now() - new Date(vehicle.createdAt).getTime()) < 30 * 86400000 && !hasPromo(vehicle) && (
          <div className="absolute top-3 right-3 bg-white text-black text-[11px] font-extrabold uppercase tracking-wide px-2.5 py-1.5 rounded-full shadow-lg" data-testid={`vehicle-new-${vehicle.slug}`}>
            {lang === "en" ? "New" : "Nové"}
          </div>
        )}
        {hasPromo(vehicle) && (
          <div
            className="absolute top-3 right-3 bg-orange-500 text-black text-[11px] font-extrabold uppercase tracking-wide px-2.5 py-1.5 rounded-full shadow-lg shadow-orange-500/40"
            data-testid={`vehicle-promo-${vehicle.slug}`}
          >
            {vehicle.promoLabel || t("promo.badge")}
          </div>
        )}

        {vehicle.multipleAvailable && (
          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-black/70 backdrop-blur-sm text-emerald-300 text-[11px] font-medium px-2.5 py-1.5 rounded-full border border-white/10" data-testid={`vehicle-availability-${vehicle.slug}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {t("vehicles.available")}
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
          <span className="inline-flex items-center gap-1.5 text-white text-sm font-medium">
            {t("vehicles.showDetail")} <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>

      {/* Thumbnails — hover (desktop) / tap (mobile) to switch main image */}
      <div className="flex gap-2 px-3 pt-3 overflow-x-auto no-scrollbar">
        {vehicle.images.map((img, idx) => (
          <button
            key={img.url}
            type="button"
            onMouseEnter={() => setActiveIndex(idx)}
            onClick={(e) => {
              e.stopPropagation();
              setActiveIndex(idx);
            }}
            className={`relative w-14 h-12 sm:w-16 sm:h-14 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
              idx === activeIndex
                ? "border-orange-500"
                : "border-transparent opacity-70 hover:opacity-100"
            }`}
            data-testid={`vehicle-thumb-${vehicle.slug}-${idx}`}
            aria-label={img.label}
          >
            <img src={imgV(img.url, "thumb")} alt={img.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>

      {/* Info */}
      <div className="p-4 sm:p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-['Cabinet_Grotesk'] text-lg sm:text-xl font-bold leading-tight">{vehicle.name}</h3>
          {!vehicle.multipleAvailable && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full whitespace-nowrap mt-0.5 bg-amber-500/10 text-amber-400 border border-amber-500/20" data-testid={`vehicle-availability-${vehicle.slug}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> {t("vehicles.onRequest")}
            </span>
          )}
        </div>
        {vehicle.trim && <p className="text-zinc-500 text-xs mt-0.5 truncate" data-testid={`vehicle-trim-${vehicle.slug}`}>{localize(vehicle.trim, lang)}</p>}

        <div className="flex flex-wrap items-center gap-1.5 mt-3 text-zinc-300 text-xs" data-testid={`vehicle-specs-${vehicle.slug}`}>
          {vehicle.year && <Spec icon={Calendar}>{vehicle.year}</Spec>}
          {vehicle.transmission && <Spec icon={Settings}>{localize(vehicle.transmission, lang)}</Spec>}
          {vehicle.fuel && <Spec icon={Fuel}>{localize(vehicle.fuel, lang)}</Spec>}
          {vehicle.seats && <Spec icon={Users}>{vehicle.seats} {t("vehicles.seats")}</Spec>}
          {vehicle.powerKw && <Spec icon={Gauge}>{vehicle.powerKw} kW</Spec>}
        </div>

        <div className="mt-4 pt-4 border-t border-white/5 flex items-end justify-between gap-3">
          <div className="min-w-0">
            {vehicle.priceMonthly == null ? (
              <p className="text-orange-400 font-bold text-base sm:text-lg">{t("price.onRequest")}</p>
            ) : (
              <>
                {(hasPromo(vehicle) || months > 1) && (
                  <p className="text-zinc-500 text-xs line-through" data-testid={`vehicle-price-original-${vehicle.slug}`}>
                    {fmtKc(hasPromo(vehicle) ? vehicle.priceOriginal : vehicle.priceMonthly)} Kč
                  </p>
                )}
                <p className="text-orange-400 font-bold text-xl sm:text-2xl leading-none" data-testid={`vehicle-price-${vehicle.slug}`}>
                  {fmtKc(vatPrice(termPrice(vehicle.priceMonthly, months), vat).main)} Kč <span className="text-sm font-semibold text-orange-400/80">{t("price.perMonth")}</span>
                </p>
                <p className="text-zinc-500 text-[11px] mt-0.5" data-testid={`vehicle-price-exvat-${vehicle.slug}`}>{fmtKc(vatPrice(termPrice(vehicle.priceMonthly, months), vat).sub)} Kč {vatPrice(termPrice(vehicle.priceMonthly, months), vat).subLabel}</p>
                <p className="text-zinc-500 text-[11px] mt-1.5 leading-snug" data-testid={`vehicle-included-${vehicle.slug}`}>
                  {months > 1 ? `${t("vehicles.atTerm")} ${months} ${t("vehicles.monthsN")} · ` : ""}{t("vehicles.included")}
                </p>
              </>
            )}
          </div>
          <Link
            to={`/vozy/${vehicle.slug}`}
            className="btn-accent text-sm px-4 py-2.5 whitespace-nowrap shrink-0"
            data-testid={`vehicle-detail-btn-${vehicle.slug}`}
          >
            {t("vehicles.detail")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export const VehiclesGallery = () => {
  const { lang, t } = useLang();
  const { vehicles } = useVehicles();
  const [filter, setFilter] = useState("all");
  const [availableOnly, setAvailableOnly] = useState(false);
  const [sortBy, setSortBy] = useState("default");
  const [months, setMonths] = useState(1);
  const vat = useVatMode();

  // Unique body types (keyed by the localized label for stability).
  const types = Array.from(
    new Map(
      vehicles.filter((v) => v.bodyType).map((v) => [localize(v.bodyType, lang), v.bodyType])
    ).entries()
  );

  let filtered =
    filter === "all" ? vehicles : vehicles.filter((v) => localize(v.bodyType, lang) === filter);

  if (availableOnly) filtered = filtered.filter((v) => v.multipleAvailable);

  if (sortBy !== "default") {
    const big = Number.MAX_SAFE_INTEGER;
    filtered = [...filtered].sort((a, b) => {
      const pa = a.priceMonthly ?? big;
      const pb = b.priceMonthly ?? big;
      return sortBy === "priceAsc" ? pa - pb : pb - pa;
    });
  }

  return (
    <section
      id="vehicles"
      className="py-16 sm:py-24 px-4 sm:px-6 bg-[#0a0a0a]"
      data-testid="vehicles-section"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 text-orange-400 font-medium tracking-widest uppercase text-xs sm:text-sm mb-3 sm:mb-4">
            <Car className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>{t("vehicles.eyebrow")}</span>
          </div>
          <h2 className="section-title text-2xl sm:text-3xl lg:text-4xl">{t("vehicles.title")}</h2>
          <p className="text-zinc-500 max-w-2xl mx-auto mt-3 sm:mt-4 text-sm sm:text-base px-2">
            {t("vehicles.subtitle")}
          </p>
          <a href="#reviews" className="inline-flex items-center gap-1.5 mt-4 text-xs sm:text-sm text-zinc-300 hover:text-white bg-white/[0.04] border border-white/10 rounded-full px-3.5 py-1.5 transition-colors" data-testid="vehicles-google-trust">
            <Star className="w-3.5 h-3.5 text-orange-400 fill-orange-400" /> {t("vehicles.googleTrust")}
          </a>
        </div>

        {/* Rental length → live price on every card */}
        <div className="flex flex-col items-center gap-2 mb-6 sm:mb-8" data-testid="vehicles-term">
          <span className="text-[11px] uppercase tracking-wider text-zinc-500">{t("vehicles.termLabel")}</span>
          <TermSwitch value={months} onChange={setMonths} />
          <p className="text-zinc-600 text-xs text-center px-4">{t("vehicles.termHint")}</p>
          <div className="inline-flex rounded-full border border-white/10 bg-white/5 p-1 mt-1" data-testid="vat-toggle">
            <button onClick={() => setVatMode("incl")} className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${vat === "incl" ? "bg-orange-500 text-white" : "text-zinc-400 hover:text-white"}`} data-testid="vat-toggle-incl">Ceny s DPH</button>
            <button onClick={() => setVatMode("excl")} className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${vat === "excl" ? "bg-orange-500 text-white" : "text-zinc-400 hover:text-white"}`} data-testid="vat-toggle-excl">bez DPH</button>
          </div>
        </div>

        {/* Type filter */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-8 sm:mb-12" data-testid="vehicles-filter">
          <button
            type="button"
            onClick={() => setFilter("all")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
              filter === "all"
                ? "border-orange-500 bg-orange-500/15 text-white"
                : "border-white/10 bg-white/5 text-zinc-400 hover:text-white hover:border-white/20"
            }`}
            data-testid="vehicles-filter-all"
          >
            {t("vehicles.filterAll")}
          </button>
          {types.map(([key, bt]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                filter === key
                  ? "border-orange-500 bg-orange-500/15 text-white"
                  : "border-white/10 bg-white/5 text-zinc-400 hover:text-white hover:border-white/20"
              }`}
              data-testid={`vehicles-filter-${key}`}
            >
              {localize(bt, lang)}
            </button>
          ))}

          <span className="hidden sm:block w-px h-6 bg-white/10 mx-1" />

          {/* Available-now toggle */}
          <button
            type="button"
            onClick={() => setAvailableOnly((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
              availableOnly
                ? "border-emerald-500 bg-emerald-500/15 text-emerald-300"
                : "border-white/10 bg-white/5 text-zinc-400 hover:text-white hover:border-white/20"
            }`}
            data-testid="vehicles-filter-available"
          >
            <span className={`w-1.5 h-1.5 rounded-full ${availableOnly ? "bg-emerald-400" : "bg-zinc-500"}`} />
            {t("vehicles.filterAvailable")}
          </button>

          {/* Sort */}
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger
              className="w-auto min-w-[170px] rounded-full h-auto px-4 py-2 text-sm font-medium border-white/10 bg-white/5 text-zinc-300 hover:border-white/20 focus:ring-orange-500/40"
              data-testid="vehicles-sort"
              aria-label={t("vehicles.sortLabel")}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0a] border-white/10 text-zinc-200">
              <SelectItem value="default" data-testid="vehicles-sort-default">{t("vehicles.sortLabel")}: {t("vehicles.sortDefault")}</SelectItem>
              <SelectItem value="priceAsc" data-testid="vehicles-sort-priceAsc">{t("vehicles.sortPriceAsc")}</SelectItem>
              <SelectItem value="priceDesc" data-testid="vehicles-sort-priceDesc">{t("vehicles.sortPriceDesc")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {filtered.length === 0 ? (
          <p className="text-center text-zinc-500 py-10" data-testid="vehicles-no-results">
            {t("vehicles.noResults")}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {filtered.map((vehicle) => (
              <VehicleCard key={vehicle.slug} vehicle={vehicle} months={months} />
            ))}
          </div>
        )}

        <p className="text-center text-zinc-400 mt-8 sm:mt-12 text-sm sm:text-base">
          {t("vehicles.footer")}
        </p>
      </div>
    </section>
  );
};

export default VehiclesGallery;
